import React, { useEffect, useState, useCallback } from "react";
import "./Sidebar.css";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getEmployeeDetails,
  getMenus,
  addMenuUsage,
} from "../../../api/ApiConfig";
import * as FaIcons from "react-icons/fa";
import * as MdIcons from "react-icons/md";
import * as IoIcons from "react-icons/io5";
import * as FcIcons from "react-icons/fc";
import Cookies from "js-cookie";

const iconLibraries = { fa: FaIcons, md: MdIcons, io: IoIcons, fc: FcIcons };

const getIconComponent = (iconName) => {
  if (!iconName) return null;
  const libKey = iconName.substring(0, 2).toLowerCase();
  return iconLibraries[libKey]?.[iconName] || null;
};

const Sidebar = () => {
  const [activeItem, setActiveItem] = useState("home");
  const [expandedMenus, setExpandedMenus] = useState({});
  const [dynamicMenuItems, setDynamicMenuItems] = useState([]);
  const [quickLinks, setQuickLinks] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [quickLinksExpanded, setQuickLinksExpanded] = useState(false);

  const navigate = useNavigate();
  const uid = Cookies.get("userid")?.match(/^(\d+)_/)?.[1];

  // =========================================================
  // PPS & TOKEN LOGIC
  // =========================================================
  const openPPS = useCallback(() => {
    window.open(
      `https://myahana.ahanait.com:8443/PPS/software/ppshome.py?var=${uid ?? ""
      }`,
      "_blank"
    );
  }, [uid]);

  const sendTokenToApp = useCallback((targetUrl) => {
    const token = Cookies.get("access_token");
    const refreshToken = Cookies.get("refresh_token");
    const userid = Cookies.get("userid");
    const email = localStorage.getItem("email");
    const emp_id = localStorage.getItem("emp_id");

    const popup = window.open(targetUrl, "_blank");
    const interval = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(interval);
        return;
      }
      popup.postMessage(
        {
          type: "TOKEN",
          token,
          refresh_token: refreshToken,
          userid,
          email,
          emp_id
        },
        new URL(targetUrl).origin
      );
      clearInterval(interval);
    }, 700);
  }, []);

  // =========================================================
  // FETCH MENUS
  // =========================================================
  useEffect(() => {
    const fetchData = async () => {
      try {
        await getEmployeeDetails();
        const menuData = await getMenus();

        const rawMenu = menuData.menu || menuData || [];
        setDynamicMenuItems(rawMenu);
        setQuickLinks(menuData.quick_links || []);
      } catch (error) {
        toast.error("Failed to fetch data");
        console.error(error);
      } finally {
        setLoadingMenu(false);
      }
    };
    fetchData();
  }, []);

  // =========================================================
  // TOKEN LISTENER
  // =========================================================
  useEffect(() => {
    const allowedOrigins = [
      "https://myahana.ahanait.com",
      "https://myahana.ahanait.com:8443",
      "https://myahana1.ahanait.com:5006",
      "https://ceo.ahanait.com:8080",
      "http://104.211.117.118:8000",
      "http://104.211.117.118:5002",
      "http://104.211.117.118:5006",
      "https://4.247.148.82:8000",
      "http://localhost:5174",
      "http://localhost:5175",
      "https://myahana1.ahanait.com:5008",
      "http://localhost:8800",
      "http://localhost:5177",
      "http://localhost:5178",
      "http://localhost:5173",
      "http://172.16.20.61:8000",
      "http://localhost:8080",
      "http://172.16.20.55:8000"
    ];
    const handler = (event) => {
      if (!allowedOrigins.some((origin) => event.origin.startsWith(origin)))
        return;
      if (event.data.type === "TOKEN") {
        const { token, userid, email, refresh_token } = event.data;
        if (token) {
          Cookies.set("access_token", token, {
            secure: true,
            sameSite: "None",
            domain: ".ahanait.com",
            path: "/",
            expires: 7,
          });
          localStorage.setItem("access_token", token);
        }
        if (refresh_token) {
          Cookies.set("refresh_token", refresh_token, {
            secure: true,
            sameSite: "None",
            domain: ".ahanait.com",
            path: "/",
            expires: 7,
          });
          localStorage.setItem("refresh_token", refresh_token);
        }
        if (email) localStorage.setItem("email", email);
        if (userid) {
          Cookies.set("userid", userid, {
            secure: true,
            sameSite: "None",
            domain: ".ahanait.com",
            path: "/",
            expires: 7,
          });
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // =========================================================
  // HELPER FUNCTION TO CHECK URL INCLUDES WITH NULL SAFETY
  // =========================================================
  const urlIncludes = (url, searchString) => {
    if (!url) return false;
    return url.includes(searchString);
  };

  // =========================================================
  // HANDLE MENU CLICK
  // =========================================================
  const handleItemClick = useCallback(
    (item, hasChildren, isParent) => {
      setActiveItem(item.slno);
      addMenuUsage({ menu_item_slno: item.slno }).catch(console.error);

      // Get token and user data
      const token = Cookies.get("access_token");
      const refreshToken = Cookies.get("refresh_token");
      const userid = Cookies.get("userid");
      const email = localStorage.getItem("email");
      const emp_id = localStorage.getItem("emp_id");

      // Check for label matches (with null safety)
      const label = item.label?.toLowerCase() || "";
      const url = item.url || "";

      if (label === "payments(pps)") {
        openPPS();
      } else if (
        label.includes("a2i_login") ||
        urlIncludes(url, "A2I_login")
      ) {
        if (!token || !email) {
          toast.error("Please ensure you are logged into Myahana");
          return;
        }
        const targetUrl = item.url;
        const targetOrigin = new URL(targetUrl).origin;
        const a2iWindow = window.open(targetUrl, "_blank");
        const interval = setInterval(() => {
          if (!a2iWindow || a2iWindow.closed) {
            clearInterval(interval);
            return;
          }
          a2iWindow.postMessage(
            {
              type: "TOKEN",
              token,
              refresh_token: refreshToken,
              email,
              userid
            },
            targetOrigin
          );
        }, 700);
      } else if (
        label.includes("ahanawpt") ||
        urlIncludes(url, "ahanawpt")
      ) {
        if (!token) {
          toast.error("Please login to Myahana first");
          console.error("❌ No access token found");
          return;
        }

        const targetUrl = item.url;
        const targetOrigin = new URL(targetUrl).origin;
        const ahanapilotWindow = window.open(targetUrl, "_blank");

        const sendInterval = setInterval(() => {
          if (!ahanapilotWindow || ahanapilotWindow.closed) {
            clearInterval(sendInterval);
            return;
          }

          try {
            ahanapilotWindow.postMessage(
              {
                type: "TOKEN",
                token: token,
                refresh_token: refreshToken,
                email: email,
                emp_id: emp_id,
                userid: userid
              },
              targetOrigin
            );
          } catch (error) {
            console.error("Error sending message to ahanapilot:", error);
          }
        }, 700);
      } else if (
        label.includes("fms") ||
        urlIncludes(url, "fms")
      ) {
        if (!token) {
          toast.error("Please login to Myahana first");
          console.error("❌ No access token found");
          return;
        }

        const targetUrl = item.url;
        const targetOrigin = new URL(targetUrl).origin;
        const fmWindow = window.open(targetUrl, "_blank");

        const sendInterval = setInterval(() => {
          if (!fmWindow || fmWindow.closed) {
            clearInterval(sendInterval);
            return;
          }

          try {
            fmWindow.postMessage(
              {
                type: "TOKEN",
                token: token,
                refresh_token: refreshToken,
                email: email,
                emp_id: emp_id,
                userid: userid
              },
              targetOrigin
            );
          } catch (error) {
            console.error("Error sending message to fmWindow:", error);
          }
        }, 700);
      } else if (
        label.includes("aribaassistlogbook") ||
        urlIncludes(url, "AribaAssistLogbook")
      ) {
        if (!token) {
          toast.error("Please login to Myahana first");
          console.error("❌ No access token found");
          return;
        }

        const targetUrl = item.url;
        const targetOrigin = new URL(targetUrl).origin;
        const ahanapilotWindow = window.open(targetUrl, "_blank");

        const sendInterval = setInterval(() => {
          if (!ahanapilotWindow || ahanapilotWindow.closed) {
            clearInterval(sendInterval);
            return;
          }

          try {
            ahanapilotWindow.postMessage(
              {
                type: "TOKEN",
                token: token,
                refresh_token: refreshToken,
                email: email,
                emp_id: emp_id,
                userid: userid
              },
              targetOrigin
            );
          } catch (error) {
            console.error("Error sending message to ahanapilot:", error);
          }
        }, 700);
      } else if (
        urlIncludes(url, "service-aggregator-portal") ||
        urlIncludes(url, "recruitment-dashboard") ||
        urlIncludes(url, "customer_rating") ||
        urlIncludes(url, "Key_account_mapping") ||
        urlIncludes(url, "Centralized-RBAC-Management") ||
        urlIncludes(url, "PMO_Dashboard") ||
        urlIncludes(url, "ProjectCodeCreation") ||
        urlIncludes(url, "Department_Rating") ||
        urlIncludes(url, "project-pulse") ||
        urlIncludes(url, "pmo_portal")
      ) {
        if (!token) {
          toast.error("Please login to Myahana first");
          console.error("❌ No access token found");
          return;
        }

        const targetUrl = item.url;
        const targetOrigin = new URL(targetUrl).origin;
        const appWindow = window.open(targetUrl, "_blank");

        const sendInterval = setInterval(() => {
          if (!appWindow || appWindow.closed) {
            clearInterval(sendInterval);
            return;
          }

          try {
            appWindow.postMessage(
              {
                type: "TOKEN",
                token: token,
                refresh_token: refreshToken,
                email: email,
                emp_id: emp_id,
                userid: userid
              },
              targetOrigin
            );
          } catch (error) {
            console.error(`Error sending message to ${targetUrl}:`, error);
          }
        }, 700);
      } else if (url) {
        // Only handle URL if it exists
        if (
          urlIncludes(url, "service-aggregator-portal") ||
          urlIncludes(url, "recruitment-dashboard") ||
          urlIncludes(url, "customer_rating") ||
          urlIncludes(url, "Key_account_mapping") ||
          urlIncludes(url, "Centralized-RBAC-Management") ||
          urlIncludes(url, "PMO_Dashboard") ||
          urlIncludes(url, "ProjectCodeCreation") ||
          urlIncludes(url, "Department_Rating") ||
          urlIncludes(url, "project-pulse") ||
          urlIncludes(url, "pmo_portal")
        ) {
          sendTokenToApp(item.url);
        } else {
          url.startsWith("/")
            ? navigate(url)
            : window.open(url, "_blank");
        }
      }

      if (hasChildren) {
        if (isParent) {
          setExpandedMenus((prev) => ({ [item.slno]: !prev[item.slno] }));
        } else {
          setExpandedMenus((prev) => ({
            ...prev,
            [item.slno]: !prev[item.slno],
          }));
        }
      }
    },
    [navigate, openPPS, sendTokenToApp]
  );

  const isParentOfActive = useCallback((item, currentId) => {
    if (item.slno === currentId) return true;
    return item.children?.some((child) => isParentOfActive(child, currentId));
  }, []);

  const renderMenuItems = useCallback(
    (items, level = 0) =>
      items.map((item) => {
        const Icon = getIconComponent(item.icon_name);
        const hasChildren = item.children?.length > 0;
        const isActive = isParentOfActive(item, activeItem);

        return (
          <React.Fragment key={item.slno}>
            <li
              className={`sidebar-item ${isActive ? "active" : ""}`}
            >
              <div
                className="sidebar-link"
                onClick={() => handleItemClick(item, hasChildren, level === 0)}
              >
                {Icon && (
                  <span className="icon">
                    <Icon />
                  </span>
                )}
                <span className="label">{item.label || "Untitled"}</span>
                {hasChildren && (
                  <span className="caret">
                    {expandedMenus[item.slno] ? (
                      <FaIcons.FaCaretDown />
                    ) : (
                      <FaIcons.FaCaretRight />
                    )}
                  </span>
                )}
              </div>
            </li>
            {hasChildren && expandedMenus[item.slno] && (
              <ul className="submenu">
                {renderMenuItems(item.children, level + 1)}
              </ul>
            )}
          </React.Fragment>
        );
      }),
    [activeItem, expandedMenus, handleItemClick, isParentOfActive]
  );

  if (loadingMenu)
    return (
      <div className="sidebar">
        <div className="sidebar-loading">
          <span>Loading menu...</span>
        </div>
      </div>
    );
  if (dynamicMenuItems.length === 0 && quickLinks.length === 0) return null;

  const homeMenu = dynamicMenuItems.find((item) => item.label === "Home");
  const otherMenus = dynamicMenuItems.filter((item) => item.label !== "Home");

  return (
    <div className="sidebar">
      <ul>
        {homeMenu && renderMenuItems([homeMenu])}
        {quickLinks.length > 0 && (
          <>
            <li
              className="sidebar-header"
              onClick={() => setQuickLinksExpanded(!quickLinksExpanded)}
            >
              <div className="sidebar-link">
                <span className="icon">
                  <FaIcons.FaStar />
                </span>
                <span className="label">Quick Links</span>
                <span className="caret">
                  {quickLinksExpanded ? (
                    <FaIcons.FaCaretDown />
                  ) : (
                    <FaIcons.FaCaretRight />
                  )}
                </span>
              </div>
            </li>
            {quickLinksExpanded && (
              <ul className="submenu">{renderMenuItems(quickLinks, 1)}</ul>
            )}
          </>
        )}
        {renderMenuItems(otherMenus)}
      </ul>
    </div>
  );
};

export default Sidebar;